import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  TextField,
  CircularProgress,
  IconButton,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import React, { useState, useEffect, useRef } from 'react'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import CopySuccessSnackbar from 'src/common/CopySuccessSnackbar'
import StarterKit from '@tiptap/starter-kit'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableHeader } from '@tiptap/extension-table-header'
import { TableCell } from '@tiptap/extension-table-cell'
import {
  RichTextEditor,
  MenuButtonBold,
  MenuButtonItalic,
  MenuButtonUnderline,
  MenuButtonBulletedList,
  MenuButtonOrderedList,
  MenuSelectHeading,
  MenuControlsContainer,
  MenuDivider,
  type RichTextEditorRef,
} from 'mui-tiptap'
import Underline from '@tiptap/extension-underline'
import { ContentCopy } from '@mui/icons-material'
import { BulkActionItem } from 'src/viewModels/workflow/BulkActionItem'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { processBulkAction, getBulkEmailDetails } from 'src/services/WorkflowService'
import { BulkInvoiceEmailResult } from 'src/viewModels/workflow/BulkInvoiceEmailResult'
import { ObjectiveBulkProcessAction } from 'src/viewModels/workflow/ObjectiveBulkProcessAction'
import moment from 'moment'
import { formatCurrency } from 'src/utils/Formatter'
import { BulkActionResult } from 'src/viewModels/workflow/BulkActionResult'
import BulkActionResultDisplay from './BulkActionResultDisplay'

interface IGenerateInvoiceEmailModalProps {
  onClose: (actionExecuted: boolean) => void
  isOpen: boolean
  userName?: string
  inboxItems: Array<BulkActionItem>
  inboxId: string
  action: InboxActionSummary
  itemType?: any
}

function GenerateInvoiceEmailModal(props: IGenerateInvoiceEmailModalProps) {
  const rteRef = useRef<RichTextEditorRef>(null)
  const [recipientsEmails, setRecipientsEmails] = useState('')
  const [CCEmails, setCCEmails] = useState('')
  const [senderName] = useState(props.userName || '')
  const [invoices, setInvoices] = useState<BulkInvoiceEmailResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copySuccess, setCopySuccess] = useState(false)
  const [copyRecipientSuccess, setCopyRecipientSuccess] = useState(false)
  const [copyCCSuccess, setCopyCCSuccess] = useState(false)
  const [pendDate, setPendDate] = useState('')
  const [pendNote, setPendNote] = useState('')
  const [pendDateTouched, setPendDateTouched] = useState(false)
  const [pendNoteTouched, setPendNoteTouched] = useState(false)
  const [resultsBulkGenerateInvoiceEmail, setResultsBulkGenerateInvoiceEmail] = useState(
    new Array<BulkActionResult>()
  )

  useEffect(() => {
    const fetchInvoices = async () => {
      const invoiceIds = props.inboxItems.map((item) => item.inboxItemId)

      if (invoiceIds.length === 0) {
        setError('No invoices selected')
        return
      }

      setLoading(true)
      setError(null)

      try {
        const bulkRequest = { invoiceInboxItemIds: invoiceIds }
        const data = await getBulkEmailDetails(bulkRequest)
        setInvoices(data)

        const uniqueEmails = [
          ...new Set(data.map((item) => item.adjusterEmail).filter(Boolean)),
        ].join('; ')
        if (uniqueEmails) {
          setRecipientsEmails(uniqueEmails)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load invoice data')
        console.error('Error fetching bulk email information:', err)
      } finally {
        setLoading(false)
      }
    }

    if (props.isOpen && props.inboxItems.length > 0) {
      fetchInvoices()
    }
  }, [props.isOpen, props.inboxItems])

  const generateEmailContent = () => {
    const tableRows = invoices
      .map(
        (inv) => `
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff; color: #000000;">${inv.patientName || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff; color: #000000;">${inv.invoiceNumber || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff; color: #000000;">${inv.isSurgical ? inv.surgeryDateString || '' : 'N/A'}</td>
        <td style="border: 1px solid #ddd; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff; color: #000000;">${inv.invoiceDateString || ''}</td>
        <td align="right" style="border: 1px solid #ddd; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff; color: #000000;">${inv.invoiceAmount != null ? formatCurrency(inv.invoiceAmount.toString()) : ''}</td>
        <td align="right" style="border: 1px solid #ddd; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff; color: #000000;">${inv.remainingBalance != null ? formatCurrency(inv.remainingBalance.toString()) : ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff; color: #000000;">${inv.insuranceClaim || ''}</td>
      </tr>
    `
      )
      .join('')

    const totalAmount = invoices.reduce((sum, inv) => sum + inv.remainingBalance, 0)

    return `
    <div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6; color: #000000; background-color: #ffffff;">
      <p style="color: #000000;">Hi [Recipient's Name],</p>
      <p style="color: #000000;">Could you please confirm whether these invoices have been received and submitted for processing? If payment has already been issued, kindly let me know.</p>
      <br/>
      <table style="border-collapse: collapse; width: 100%; border: 1px solid #ddd; font-family: Arial, sans-serif; font-size: 14px; background-color: #ffffff;">
        <thead>
          <tr>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; font-family: Arial, sans-serif; font-size: 14px; color: #000000;">Patient Name</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; font-family: Arial, sans-serif; font-size: 14px; color: #000000;">Invoice Number</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; font-family: Arial, sans-serif; font-size: 14px; color: #000000;">Surgery Date</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; font-family: Arial, sans-serif; font-size: 14px; color: #000000;">Invoice Date</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; text-align: right; font-family: Arial, sans-serif; font-size: 14px; color: #000000;">Invoice Amount</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; text-align: right; font-family: Arial, sans-serif; font-size: 14px; color: #000000;">Remaining Balance</th>
            <th style="border: 1px solid #ddd; padding: 8px; background-color: #f2f2f2; font-weight: bold; font-family: Arial, sans-serif; font-size: 14px; color: #000000;">Insurance Claim #</th>
          </tr>
        </thead>
        <tbody>
            ${tableRows}
          <tr style="background-color: #fafafa; border-top: 2px solid #000;">
            <td colspan="5" style="border: 1px solid #000; padding: 8px; font-weight: bold; font-family: Arial, sans-serif; font-size: 14px; background-color: #fafafa; color: #000000;">Total Outstanding:</td>
            <td align="right" style="border: 1px solid #000; padding: 8px; font-weight: bold; text-align: right; font-family: Arial, sans-serif; font-size: 14px; background-color: #fafafa; color: #000000;">${formatCurrency(totalAmount.toString())}</td>
            <td style="border: 1px solid #000; padding: 8px; font-family: Arial, sans-serif; font-size: 14px; background-color: #fafafa;"></td>
          </tr>
        </tbody>
      </table>
      <br/>
      <p style="color: #000000;">Feel free to reach out with any questions or concerns. Thank you!</p>
      <br/>
      <p style="color: #000000;">Best regards,<br>${senderName || '[Your Name]'}</p>
    </div>
    `
  }

  useEffect(() => {
    generateEmailContent
    if (rteRef.current?.editor && invoices.length > 0) {
      rteRef.current.editor.commands.setContent(generateEmailContent())
    }
  }, [recipientsEmails, senderName, invoices])

  useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === 'c' &&
        props.isOpen &&
        !loading &&
        invoices.length > 0
      ) {
        e.preventDefault()
        await handleCopyHTML()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [props.isOpen, loading, invoices])

  const handleClose = () => {
    const actionExecuted =
      resultsBulkGenerateInvoiceEmail && resultsBulkGenerateInvoiceEmail.length > 0
    props.onClose(actionExecuted)
  }

  const hydrateHtmlContent = async (): Promise<string | null> => {
    const emailContent = rteRef.current?.editor?.getHTML()
    if (!emailContent) return null

    const hydratedBox = document.getElementById('hydrated-html')
    if (!hydratedBox) return null

    hydratedBox.innerHTML = emailContent
    await new Promise((resolve) => setTimeout(resolve, 100))
    return hydratedBox.innerHTML
  }

  const handleCopyHTML = async () => {
    const formattedHtml = await hydrateHtmlContent()
    if (!formattedHtml) return

    await copyHtmlToClipboard('hydrated-html')
    setCopySuccess(true)
  }

  const copyHtmlToClipboard = async (elementId: string): Promise<void> => {
    try {
      const element = document.getElementById(elementId)
      if (!element) {
        throw new Error(`Element with id "${elementId}" not found`)
      }

      const range = document.createRange()
      range.selectNodeContents(element)

      const selection = window.getSelection()
      if (!selection) {
        throw new Error('Could not get selection')
      }

      selection.removeAllRanges()
      selection.addRange(range)

      const success = document.execCommand('copy')

      selection.removeAllRanges()

      if (!success) {
        const htmlContent = element.innerHTML
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([element.innerText], { type: 'text/plain' }),
        })
        await navigator.clipboard.write([clipboardItem])
      }
    } catch (error) {
      console.error('Failed to copy HTML:', error)
      throw error
    }
  }

  const handleCopyRecipient = async () => {
    await navigator.clipboard
      .writeText(recipientsEmails)
      .then(() => setCopyRecipientSuccess(true))
      .catch(() => {})
  }

  const handleCopyCC = async () => {
    await navigator.clipboard
      .writeText(CCEmails)
      .then(() => setCopyCCSuccess(true))
      .catch(() => {})
  }

  const handleGenerate = async () => {
    if (!pendDate || !pendNote) {
      setPendDateTouched(true)
      setPendNoteTouched(true)
      setError('Pend Date and Pend Note are required')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const emailContent = rteRef.current?.editor?.getHTML()
      if (!emailContent) {
        setError('Failed to generate email content')
        setLoading(false)
        return
      }

      const emailWithMetadata = `
        <p style="margin: 4px 0;"><strong>To:</strong> ${recipientsEmails}</p>
        <p style="margin: 4px 0;"><strong>CC:</strong> ${CCEmails}</p>
        ${emailContent}
      `

      const data = new ObjectiveBulkProcessAction()
      data.comment = ''
      data.bulkInboxItems = props.inboxItems
      data.pendDate = new Date(moment(pendDate, 'YYYY-MM-DD').toISOString())
      data.pendNote = pendNote
      data.invoiceEmailHtmlBody = emailWithMetadata

      var results = await processBulkAction(data, props.action.id.toString())
      setResultsBulkGenerateInvoiceEmail(results)
    } catch (err) {
      setError('Failed to process action')
      console.error('Error in handleGenerate:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth="lg"
      open={props.isOpen}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          handleClose()
        }
      }}
    >
      <DialogTitle style={{ padding: 0 }}>
        <ResponsiveAppBar
          title={`Generate Invoice Email (${props.inboxItems.length} ${
            props.inboxItems.length === 1 ? 'Invoice' : 'Invoices'
          })`}
          isModal={true}
        />
      </DialogTitle>
      <DialogContent style={{ padding: '24px' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ padding: 2, color: 'error.main' }}>
            Error loading invoices: {error}
          </Box>
        ) : resultsBulkGenerateInvoiceEmail &&
          resultsBulkGenerateInvoiceEmail.length > 0 ? (
          <BulkActionResultDisplay results={resultsBulkGenerateInvoiceEmail} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label="Recipient(s)"
                    value={recipientsEmails}
                    onChange={(e) => setRecipientsEmails(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleCopyRecipient}
                    color="primary"
                    title="Copy recipient emails"
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    label="CC"
                    value={CCEmails}
                    onChange={(e) => setCCEmails(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleCopyCC}
                    color="primary"
                    title="Copy CC emails"
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 0.5 }}>
                <DatePicker
                  label="Pend Date"
                  value={pendDate ? moment(pendDate) : null}
                  onChange={(date) =>
                    setPendDate(date ? moment(date).format('YYYY-MM-DD') : '')
                  }
                  slotProps={{
                    textField: {
                      size: 'small',
                      required: true,
                      error: pendDateTouched && !pendDate,
                      helperText:
                        pendDateTouched && !pendDate ? 'Pend Date Required' : '',
                      onBlur: () => setPendDateTouched(true),
                      sx: { width: '250px' },
                      InputLabelProps: { shrink: true },
                    },
                    openPickerIcon: { fontSize: 'small' },
                  }}
                />
                <TextField
                  InputLabelProps={{ shrink: true }}
                  label="Pend Note"
                  value={pendNote}
                  onChange={(e) => setPendNote(e.target.value)}
                  onBlur={() => setPendNoteTouched(true)}
                  placeholder="Enter pend note"
                  fullWidth
                  required
                  error={pendNoteTouched && !pendNote}
                  helperText={pendNoteTouched && !pendNote ? 'Pend Note Required' : ''}
                  multiline
                  rows={3}
                />
              </Box>
            </Box>
            <Box
              sx={{
                border: '1px solid #ccc',
                borderRadius: '4px',
                '& .ProseMirror': {
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                  },
                  '& thead th, & th': {
                    border: '1px solid #ddd !important',
                    padding: '8px !important',
                    fontFamily: 'Arial, sans-serif !important',
                    fontSize: '14px !important',
                    backgroundColor: '#f2f2f2 !important',
                    fontWeight: 'bold !important',
                    backgroundImage: 'none !important',
                    color: '#000 !important',
                  },
                  '& tbody td': {
                    border: '1px solid #ddd !important',
                    padding: '8px !important',
                    fontFamily: 'Arial, sans-serif !important',
                    fontSize: '14px !important',
                  },
                  '& tbody td:nth-of-type(5), & tbody td:nth-of-type(6)': {
                    textAlign: 'right !important',
                  },
                  '& tbody tr:last-child td:nth-of-type(2)': {
                    textAlign: 'right !important',
                  },
                  '& tfoot tr td': {
                    border: '1px solid #000 !important',
                    padding: '8px !important',
                    fontFamily: 'Arial, sans-serif !important',
                    fontSize: '14px !important',
                    fontWeight: 'bold !important',
                    backgroundColor: '#fafafa !important',
                    direction: 'ltr !important',
                  },
                  '& p': {
                    margin: '8px 0',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                  },
                },
              }}
            >
              <RichTextEditor
                ref={rteRef}
                extensions={[
                  Table.configure({
                    resizable: true,
                  }),
                  TableRow,
                  TableHeader,
                  TableCell,
                  StarterKit,
                  Underline,
                ]}
                content={generateEmailContent()}
                renderControls={() => (
                  <MenuControlsContainer>
                    <MenuSelectHeading />
                    <MenuDivider />
                    <MenuButtonBold />
                    <MenuButtonItalic />
                    <MenuButtonUnderline />
                    <MenuDivider />
                    <MenuButtonBulletedList />
                    <MenuButtonOrderedList />
                  </MenuControlsContainer>
                )}
              />
            </Box>
            <Box
              id="hydrated-html"
              sx={{
                opacity: 0,
                position: 'absolute',
                pointerEvents: 'none',
                zIndex: -1,
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%',
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
                backgroundColor: '#ffffff !important',
                color: '#000000 !important',
                '& *': {
                  colorScheme: 'light !important',
                },
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  backgroundColor: '#ffffff !important',
                },
                '& thead th, & th': {
                  border: '1px solid #ddd !important',
                  padding: '8px !important',
                  fontFamily: 'Arial, sans-serif !important',
                  fontSize: '14px !important',
                  backgroundColor: '#f2f2f2 !important',
                  fontWeight: 'bold !important',
                  whiteSpace: 'nowrap',
                  color: '#000000 !important',
                },
                '& tbody td': {
                  border: '1px solid #ddd !important',
                  padding: '8px !important',
                  fontFamily: 'Arial, sans-serif !important',
                  fontSize: '14px !important',
                  whiteSpace: 'nowrap',
                  backgroundColor: '#ffffff !important',
                  color: '#000000 !important',
                },
                '& tbody td:nth-of-type(5), & tbody td:nth-of-type(6)': {
                  textAlign: 'right !important',
                },
                '& tbody tr:last-child td:nth-of-type(2)': {
                  textAlign: 'right !important',
                },
                '& tfoot tr td': {
                  border: '1px solid #000 !important',
                  padding: '8px !important',
                  fontFamily: 'Arial, sans-serif !important',
                  fontSize: '14px !important',
                  fontWeight: 'bold !important',
                  backgroundColor: '#fafafa !important',
                  whiteSpace: 'nowrap',
                  direction: 'ltr !important',
                  color: '#000000 !important',
                },
                '& p': {
                  margin: '8px 0',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '14px',
                  color: '#000000 !important',
                },
                '& div': {
                  backgroundColor: '#ffffff !important',
                  color: '#000000 !important',
                },
              }}
            ></Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {!(
          resultsBulkGenerateInvoiceEmail && resultsBulkGenerateInvoiceEmail.length > 0
        ) && (
          <>
            <Button
              onClick={handleCopyHTML}
              color="secondary"
              variant="contained"
              startIcon={<ContentCopy />}
              disabled={loading}
            >
              Copy Format
            </Button>
            <Button
              onClick={handleGenerate}
              color="primary"
              variant="contained"
              disabled={loading || !pendDate || !pendNote}
            >
              Execute
            </Button>
          </>
        )}
        <Button onClick={handleClose} color="primary">
          Close
        </Button>
      </DialogActions>
      <CopySuccessSnackbar
        open={copySuccess}
        onClose={() => setCopySuccess(false)}
        message="Email format copied to clipboard! You can now paste it into Outlook."
      />
      <CopySuccessSnackbar
        open={copyRecipientSuccess}
        onClose={() => setCopyRecipientSuccess(false)}
        message="Recipient name copied to clipboard!"
      />
      <CopySuccessSnackbar
        open={copyCCSuccess}
        onClose={() => setCopyCCSuccess(false)}
        message="CC name copied to clipboard!"
      />
    </Dialog>
  )
}

export default GenerateInvoiceEmailModal
